import type { ActionFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type { ShouldRevalidateFunction } from 'react-router';
import { z } from 'zod';
import { reportUser } from '~/modules/moderation/api/report-user.server';

export const REPORT_USER_API_ROUTE = '/remix-api/moderation/report-user';

const FORM_DATA_SCHEMA = z.object({
    userID: z.coerce.number().int().positive(),
    moderationReasonID: z.coerce.number().int().positive(),
    note: z.string().trim().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
});

/**
 * API endpoint to report a user for moderation.
 * Use with the `useUserModeration` hook.
 * @see {@link packages/web/app/modules/moderation/hooks/use-user-moderation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const payload = await request.json();
    const result = FORM_DATA_SCHEMA.safeParse(payload);

    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    try {
        const response = await reportUser({
            context,
            userID: result.data.userID,
            moderationReasonID: result.data.moderationReasonID,
            note: result.data.note,
            metadata: result.data.metadata,
        });

        if (!response) {
            return routerData(null, { status: 500 });
        }

        let statusCode = 201;
        if (
            response.__typename === 'NotAuthorizedError' ||
            response.__typename === 'NotFoundError'
        ) {
            statusCode = 403;
        } else if (response.__typename === 'UserError') {
            statusCode = 400;
        } else if (response.__typename === 'ServerError') {
            statusCode = 500;
        }

        return routerData({ response }, { status: statusCode });
    } catch (error) {
        logger.error(`Error in ${REPORT_USER_API_ROUTE}`, { error });
        return routerData(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
